import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Check, ShieldCheck, Sparkles, ArrowRight, Car, UtensilsCrossed, Film, Gift, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const PLAN_COLORS = {
    blue: 'bg-inorbit-blue text-white',
    yellow: 'bg-inorbit-yellow text-inorbit-blue',
    orange: 'bg-inorbit-orange text-white',
};

const PERK_ICON = (perk) => {
    const p = perk.toLowerCase();
    if (p.includes('valet') || p.includes('parking')) return Car;
    if (p.includes('restaurant') || p.includes('dessert')) return UtensilsCrossed;
    if (p.includes('movie') || p.includes('tickets')) return Film;
    if (p.includes('gift') || p.includes('hamper')) return Gift;
    if (p.includes('birthday')) return Heart;
    return Sparkles;
};

export default function MallPass() {
    const [plans, setPlans] = useState([]);
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(null);
    const { type } = useAuth();

    const reload = () => {
        api.get('/mall-pass/plans').then(({ data }) => setPlans(data)).catch(() => {});
        if (type === 'customer') api.get('/customer/membership').then(({ data }) => setActive(data?.status === 'active' ? data : null)).catch(() => {});
    };
    useEffect(reload, [type]);

    const subscribe = async (planId) => {
        if (type !== 'customer') { toast.error('Sign in to subscribe'); return; }
        setBusy(planId);
        try {
            const { data } = await api.post('/mall-pass/subscribe', { plan_id: planId });
            setActive(data);
            toast.success(`Welcome to ${data.plan_name}! Card ${data.card_no}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Subscription failed');
        } finally { setBusy(null); }
    };

    return (
        <div data-testid="mall-pass-page" className="bg-cream min-h-screen">
            {/* HERO */}
            <section className="relative bg-inorbit-blue-deeper text-white pt-28 pb-16 overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[600px] h-[600px] border-2 border-inorbit-yellow/15 rounded-full orbit-slow" />
                <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] border border-inorbit-orange/20 rounded-full orbit-fast" />
                <div className="relative max-w-6xl mx-auto px-6 md:px-12">
                    <div className="overline text-inorbit-yellow mb-3 flex items-center gap-2"><Crown size={12} /> The Mall Pass</div>
                    <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-none" style={{ color: 'white' }}>
                        One pass.<br />
                        <span className="script-tag text-inorbit-yellow">Six malls. Every perk.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed">
                        Skip the queues, skip the parking fees, skip the wait. Inorbit One+ pays itself back in ~3 visits — and turns every mall trip into a five-star concierge experience.
                    </p>
                    {active && (
                        <div className="mt-8 inline-block bg-inorbit-yellow text-inorbit-blue px-6 py-3 rounded-md" data-testid="active-membership">
                            <div className="text-[10px] uppercase tracking-widest font-bold">Your active pass</div>
                            <div className="font-serif text-2xl">{active.plan_name}</div>
                            <div className="text-xs mt-1 opacity-80">Card {active.card_no} · expires {new Date(active.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        </div>
                    )}
                </div>
            </section>

            {/* PLANS */}
            <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-6" data-testid="mall-pass-plans">
                {plans.map((p, i) => {
                    const isActive = active?.plan_id === p.id;
                    return (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.1 }}
                            className={`relative card-luxury p-7 overflow-hidden ${p.is_popular ? 'ring-2 ring-inorbit-yellow scale-[1.02]' : ''}`}
                            data-testid={`plan-${p.id}`}
                        >
                            {p.is_popular && (
                                <div className="absolute top-4 right-4 pill pill-yellow">Most popular</div>
                            )}
                            <div className={`inline-flex w-12 h-12 rounded-full items-center justify-center mb-4 ${PLAN_COLORS[p.badge_color] || PLAN_COLORS.blue}`}>
                                <Crown size={20} strokeWidth={1.8} />
                            </div>
                            <h3 className="font-serif text-2xl text-inorbit-blue leading-tight">{p.name}</h3>
                            <div className="italic text-text-secondary text-sm mt-1 mb-5">{p.tagline}</div>
                            <div className="flex items-baseline gap-2 mb-5">
                                <span className="font-serif text-5xl text-inorbit-blue">₹{p.price.toLocaleString('en-IN')}</span>
                                <span className="text-sm text-text-tertiary">/ {p.billing}</span>
                            </div>
                            <ul className="space-y-2.5 mb-7">
                                {p.perks.map((perk) => {
                                    const I = PERK_ICON(perk);
                                    return (
                                        <li key={perk} className="flex items-start gap-3 text-sm">
                                            <I size={14} className="text-inorbit-orange mt-0.5 shrink-0" />
                                            <span className="text-text-secondary leading-snug">{perk}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <button
                                onClick={() => subscribe(p.id)}
                                disabled={busy === p.id || isActive}
                                className={`w-full justify-center ${isActive ? 'btn-outline' : p.is_popular ? 'btn-primary' : 'btn-outline border-inorbit-blue text-inorbit-blue hover:bg-inorbit-blue hover:text-white'} disabled:opacity-50`}
                                data-testid={`subscribe-${p.id}`}
                            >
                                {isActive ? <><Check size={14} /> Active</> : busy === p.id ? 'Processing…' : <>Start now <ArrowRight size={14} /></>}
                            </button>
                        </motion.div>
                    );
                })}
            </section>

            {/* TRUST STRIP */}
            <section className="bg-inorbit-blue text-white py-12">
                <div className="max-w-6xl mx-auto px-6 md:px-12 grid sm:grid-cols-3 gap-6 text-center">
                    {[
                        ['Pays for itself', '3 mall visits = membership recouped.', ShieldCheck],
                        ['Cancel anytime', 'No lock-ins. Prorated refund within 30 days.', Sparkles],
                        ['All 6 malls', 'Malad · Vashi · Cyberabad · Vadodara · Hubballi · Visakhapatnam.', Crown],
                    ].map(([t, b, I]) => (
                        <div key={t}>
                            <I size={26} className="text-inorbit-yellow mx-auto mb-3" />
                            <div className="font-serif text-2xl mb-1" style={{ color: 'white' }}>{t}</div>
                            <div className="text-sm text-white/75">{b}</div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
